import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Stack,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  IconButton,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  MusicNote as MusicIcon,
  VideoLibrary as VideoIcon,
  PlaylistAdd as PlaylistIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Album as AlbumIcon
} from '@mui/icons-material';
import { useParams, useHistory } from 'react-router-dom';
import ApiService from '../../services/api-service';
import notifier from '../../components/Notifier';
import Loader from '../../components/Loader';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function UserEdit() {
  const { userId } = useParams();
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // User data
  const [userData, setUserData] = useState({});
  const [editData, setEditData] = useState({});
  const [profileImg, setProfileImg] = useState(null);
  const [coverImg, setCoverImg] = useState(null);
  
  // Content stats
  const [stats, setStats] = useState(null);
  
  // Content lists
  const [userContent, setUserContent] = useState({ music: null, videos: null, playlists: null });
  const [contentLoading, setContentLoading] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [userStudios, setUserStudios] = useState([]);
  
  // Dialogs
  const [musicDialog, setMusicDialog] = useState(false);
  const [videoDialog, setVideoDialog] = useState(false);
  const [studioDialog, setStudioDialog] = useState(false);
  const [albumDialog, setAlbumDialog] = useState(false);
  
  // Upload forms
  const [musicForm, setMusicForm] = useState({
    title: '',
    genre: '',
    releaseDate: new Date().toISOString().split('T')[0],
    isExplicit: false,
    private: false,
    isNFT: false,
    nftQuantity: 0,
    collaborators: []
  });
  
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    private: false,
    studioId: ''
  });
  
  const [studioForm, setStudioForm] = useState({
    name: '',
    description: '',
    isPrivate: false
  });

  useEffect(() => {
    console.log('UserEdit mounted with userId:', userId);
    if (userId) {
      fetchUserData();
      fetchUserStats();
    } else {
      notifier.error('Invalid user ID');
      history.push('/admin/users');
    }
  }, [userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      console.log('Fetching user data for userId:', userId);
      
      // Use ADMIN_GET_ALL_USERS with search by ID
      const searchResponse = await ApiService.ADMIN_GET_ALL_USERS({ query: userId });
      console.log('Search response:', searchResponse);
      
      if (searchResponse.result && searchResponse.result.response && searchResponse.result.response.length > 0) {
        // The response structure is { result: { response: [...users] } }
        const users = searchResponse.result.response;
        const user = users.find(u => u._id === userId);
        
        if (user) {
          console.log('Found user:', user);
          setUserData(user);
          setEditData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            displayArtistName: user.displayArtistName || '',
            bio: user.bio || '',
            country: user.country || '',
            gender: user.gender || '',
            instagramLink: user.instagramLink || '',
            tikTokLink: user.tikTokLink || '',
            twitterLink: user.twitterLink || '',
            isVerified: user.isVerified || false,
            subscriptionFee: user.subscriptionFee || 0,
            subscriptionStatus: user.subscriptionStatus || 'disabled'
          });
        } else {
          console.error('User not found in search results');
          notifier.error('User not found');
          setTimeout(() => history.push('/admin/users'), 2000);
        }
      } else {
        console.error('No users found in search response');
        notifier.error('User not found');
        setTimeout(() => history.push('/admin/users'), 2000);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      notifier.error('Failed to fetch user data');
      // Don't redirect on error, let user retry
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await ApiService.ADMIN_GET_USER_STATS(userId);
      setStats(response.result);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUserContent = async (contentType = 'all') => {
    try {
      setContentLoading(true);
      console.log('Fetching user content for userId:', userId, 'contentType:', contentType);
      const response = await ApiService.ADMIN_GET_USER_CONTENT(userId, contentType);
      console.log('User content response:', response);
      console.log('Response result:', response.result);
      setUserContent(response.result);
    } catch (error) {
      console.error('Error fetching user content:', error);
      notifier.error('Failed to fetch user content');
    } finally {
      setContentLoading(false);
    }
  };

  const fetchUserStudios = async () => {
    try {
      const response = await ApiService.ADMIN_GET_USER_CONTENT(userId, 'playlists');
      if (response.result && response.result.playlists) {
        setUserStudios(response.result.playlists.items);
      }
    } catch (error) {
      console.error('Error fetching user studios:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      // Add text fields, filtering out empty gender
      Object.keys(editData).forEach(key => {
        // Skip empty gender value
        if (key === 'gender' && editData[key] === '') {
          return;
        }
        formData.append(key, editData[key]);
      });
      
      // Add images if selected
      if (profileImg) {
        formData.append('profileImg', profileImg);
      }
      if (coverImg) {
        formData.append('coverImg', coverImg);
      }
      
      await ApiService.ADMIN_UPDATE_USER_PROFILE(userId, formData);
      notifier.success('User profile updated successfully');
      fetchUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      notifier.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadMusic = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      // Add form fields
      Object.keys(musicForm).forEach(key => {
        if (key === 'collaborators') {
          // Stringify array fields for FormData
          formData.append(key, JSON.stringify(musicForm[key]));
        } else {
          formData.append(key, musicForm[key]);
        }
      });
      
      // Add files
      const fileInput = document.getElementById('music-file');
      const thumbnailInput = document.getElementById('music-thumbnail');
      
      if (!fileInput.files[0] || !thumbnailInput.files[0]) {
        notifier.error('Please select both music file and thumbnail');
        return;
      }
      
      formData.append('file', fileInput.files[0]);
      formData.append('thumbnailImg', thumbnailInput.files[0]);
      
      await ApiService.ADMIN_UPLOAD_MUSIC_FOR_USER(userId, formData);
      notifier.success('Music uploaded successfully');
      setMusicDialog(false);
      setMusicForm({
        title: '',
        genre: '',
        releaseDate: new Date().toISOString().split('T')[0],
        isExplicit: false,
        private: false,
        isNFT: false,
        nftQuantity: 0,
        collaborators: []
      });
      fetchUserStats();
      if (userContent.music || selectedContentType === 'music' || selectedContentType === 'all') {
        fetchUserContent(selectedContentType);
      }
    } catch (error) {
      console.error('Error uploading music:', error);
      notifier.error('Failed to upload music');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadVideo = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      // Add form fields
      Object.keys(videoForm).forEach(key => {
        formData.append(key, videoForm[key]);
      });
      
      // Add files
      const fileInput = document.getElementById('video-file');
      const thumbnailInput = document.getElementById('video-thumbnail');
      
      if (!fileInput.files[0] || !thumbnailInput.files[0]) {
        notifier.error('Please select both video file and thumbnail');
        return;
      }
      
      formData.append('file', fileInput.files[0]);
      formData.append('thumbnailImg', thumbnailInput.files[0]);
      
      await ApiService.ADMIN_UPLOAD_VIDEO_FOR_USER(userId, formData);
      notifier.success('Video uploaded successfully');
      setVideoDialog(false);
      setVideoForm({
        title: '',
        description: '',
        private: false,
        studioId: ''
      });
      fetchUserStats();
      if (userContent.videos || selectedContentType === 'videos' || selectedContentType === 'all') {
        fetchUserContent(selectedContentType);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      notifier.error('Failed to upload video');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStudio = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      // Add form fields
      Object.keys(studioForm).forEach(key => {
        formData.append(key, studioForm[key]);
      });
      
      // Add thumbnail if selected
      const thumbnailInput = document.getElementById('studio-thumbnail');
      if (thumbnailInput.files[0]) {
        formData.append('thumbnailImg', thumbnailInput.files[0]);
      }
      
      await ApiService.ADMIN_CREATE_STUDIO_FOR_USER(userId, formData);
      notifier.success('Studio created successfully');
      setStudioDialog(false);
      setStudioForm({
        name: '',
        description: '',
        isPrivate: false
      });
      fetchUserStats();
      if (userContent.playlists || selectedContentType === 'playlists' || selectedContentType === 'all') {
        fetchUserContent(selectedContentType);
      }
    } catch (error) {
      console.error('Error creating studio:', error);
      notifier.error('Failed to create studio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContent = async (contentType, contentId) => {
    if (!window.confirm(`Are you sure you want to delete this ${contentType}?`)) {
      return;
    }
    
    try {
      await ApiService.ADMIN_DELETE_USER_CONTENT(userId, contentType, contentId);
      notifier.success(`${contentType} deleted successfully`);
      fetchUserStats();
      fetchUserContent(selectedContentType); // Refresh content list
    } catch (error) {
      console.error('Error deleting content:', error);
      notifier.error(`Failed to delete ${contentType}`);
    }
  };

  console.log('UserEdit render - userData:', userData, 'editData:', editData);

  if (loading) {
    return <Loader />;
  }

  if (!userData._id) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5">No user data available</Typography>
        <Button 
          variant="outlined" 
          onClick={() => history.push('/admin/users')}
          sx={{ mt: 2 }}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Edit User</Typography>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={() => history.push('/admin/users')}
        >
          Back to Users
        </Button>
      </Stack>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => {
          setTabValue(v);
          if (v === 1 && !userContent.music && !userContent.videos && !userContent.playlists) {
            fetchUserContent();
          }
        }}>
          <Tab label="Profile" />
          <Tab label="Content" />
          <Tab label="Statistics" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Profile Images */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Profile Image</Typography>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Avatar
                      src={profileImg ? URL.createObjectURL(profileImg) : userData.profileImg?.filePath}
                      sx={{ width: 150, height: 150, mx: 'auto', mb: 2 }}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                    >
                      Change Profile Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => setProfileImg(e.target.files[0])}
                      />
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>Cover Image</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 120,
                        bgcolor: 'grey.300',
                        backgroundImage: coverImg 
                          ? `url(${URL.createObjectURL(coverImg)})` 
                          : userData.coverImg?.filePath 
                          ? `url(${userData.coverImg.filePath})`
                          : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        mb: 2,
                        borderRadius: 1
                      }}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                    >
                      Change Cover Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => setCoverImg(e.target.files[0])}
                      />
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Profile Fields */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Profile Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editData.firstName}
                        onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editData.lastName}
                        onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Display Artist Name"
                        value={editData.displayArtistName}
                        onChange={(e) => setEditData({ ...editData, displayArtistName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Bio"
                        value={editData.bio}
                        onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={editData.country}
                        onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Gender"
                        value={editData.gender}
                        onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                        SelectProps={{ native: true }}
                      >
                        <option value="">Select</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        value={editData.instagramLink}
                        onChange={(e) => setEditData({ ...editData, instagramLink: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="TikTok"
                        value={editData.tikTokLink}
                        onChange={(e) => setEditData({ ...editData, tikTokLink: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Twitter"
                        value={editData.twitterLink}
                        onChange={(e) => setEditData({ ...editData, twitterLink: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editData.isVerified}
                            onChange={(e) => setEditData({ ...editData, isVerified: e.target.checked })}
                          />
                        }
                        label="Verified Artist"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Subscription Fee"
                        value={editData.subscriptionFee}
                        onChange={(e) => setEditData({ ...editData, subscriptionFee: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Subscription Status"
                        value={editData.subscriptionStatus}
                        onChange={(e) => setEditData({ ...editData, subscriptionStatus: e.target.value })}
                        SelectProps={{ native: true }}
                      >
                        <option value="disabled">Disabled</option>
                        <option value="enabled">Enabled</option>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      Save Profile
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack direction="row" spacing={2} mb={3}>
            <Button
              variant="contained"
              startIcon={<MusicIcon />}
              onClick={() => setMusicDialog(true)}
            >
              Upload Music
            </Button>
            <Button
              variant="contained"
              startIcon={<VideoIcon />}
              onClick={() => {
                fetchUserStudios();
                setVideoDialog(true);
              }}
            >
              Upload Video
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AlbumIcon />}
              onClick={() => setAlbumDialog(true)}
            >
              Bulk Album Upload
            </Button>
            <Button
              variant="contained"
              startIcon={<PlaylistIcon />}
              onClick={() => setStudioDialog(true)}
            >
              Create Studio
            </Button>
          </Stack>

          {/* Content Type Filter */}
          <Box mb={3}>
            <Tabs value={selectedContentType} onChange={(e, v) => {
              setSelectedContentType(v);
              fetchUserContent(v);
            }}>
              <Tab label="All Content" value="all" />
              <Tab label="Music" value="music" />
              <Tab label="Videos" value="videos" />
              <Tab label="Playlists" value="playlists" />
            </Tabs>
          </Box>

          {contentLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Music Section */}
              {(selectedContentType === 'all' || selectedContentType === 'music') && userContent.music && (
                <Box mb={4}>
                  <Typography variant="h6" gutterBottom>
                    Music ({userContent.music.total})
                  </Typography>
                  <Grid container spacing={2}>
                    {userContent.music.items.map((music) => (
                      <Grid item xs={12} sm={6} md={4} key={music._id}>
                        <Card>
                          <Box
                            sx={{
                              height: 200,
                              backgroundImage: `url(${music.thumbnailImg?.filePath || 'https://via.placeholder.com/300x200'})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              position: 'relative'
                            }}
                          >
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                              }}
                              onClick={() => handleDeleteContent('music', music._id)}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Box>
                          <CardContent>
                            <Typography variant="subtitle1" noWrap>{music.title}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {music.artist}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {music.listens || 0} listens • {music.genre}
                            </Typography>
                            {music.private && (
                              <Chip label="Private" size="small" color="warning" sx={{ mt: 1 }} />
                            )}
                            {music.isExplicit && (
                              <Chip label="Explicit" size="small" color="error" sx={{ mt: 1, ml: 1 }} />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Videos Section */}
              {(selectedContentType === 'all' || selectedContentType === 'videos') && userContent.videos && (
                <Box mb={4}>
                  <Typography variant="h6" gutterBottom>
                    Videos ({userContent.videos.total})
                  </Typography>
                  <Grid container spacing={2}>
                    {userContent.videos.items.map((video) => (
                      <Grid item xs={12} sm={6} md={4} key={video._id}>
                        <Card>
                          <Box
                            sx={{
                              height: 200,
                              backgroundImage: `url(${video.thumbnailImg?.filePath || 'https://via.placeholder.com/300x200'})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              position: 'relative'
                            }}
                          >
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                              }}
                              onClick={() => handleDeleteContent('video', video._id)}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Box>
                          <CardContent>
                            <Typography variant="subtitle1" noWrap>{video.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {video.views || 0} views
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {video.description}
                            </Typography>
                            {video.private && (
                              <Chip label="Private" size="small" color="warning" sx={{ mt: 1 }} />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Playlists Section */}
              {(selectedContentType === 'all' || selectedContentType === 'playlists') && userContent.playlists && (
                <Box mb={4}>
                  <Typography variant="h6" gutterBottom>
                    Playlists ({userContent.playlists.total})
                  </Typography>
                  <Grid container spacing={2}>
                    {userContent.playlists.items.map((playlist) => (
                      <Grid item xs={12} sm={6} md={4} key={playlist._id}>
                        <Card>
                          <Box
                            sx={{
                              height: 200,
                              backgroundImage: `url(${playlist.thumbnailImg?.filePath || 'https://via.placeholder.com/300x200'})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              position: 'relative'
                            }}
                          >
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                              }}
                              onClick={() => handleDeleteContent('playlist', playlist._id)}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Box>
                          <CardContent>
                            <Typography variant="subtitle1" noWrap>{playlist.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {playlist.music ? playlist.music.length : 0} songs
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {playlist.description}
                            </Typography>
                            {playlist.private && (
                              <Chip label="Private" size="small" color="warning" sx={{ mt: 1 }} />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* No content message */}
              {!contentLoading && userContent && 
               (!userContent.music?.items?.length && !userContent.videos?.items?.length && !userContent.playlists?.items?.length) && (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="text.secondary">
                    No content found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This user hasn't uploaded any content yet.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {stats && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Followers</Typography>
                    <Typography variant="h3">{stats.user.followers}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Following</Typography>
                    <Typography variant="h3">{stats.user.following}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Streaming Balance</Typography>
                    <Typography variant="h3">{stats.user.streamingBalance}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Paper>

      {/* Music Upload Dialog */}
      <Dialog open={musicDialog} onClose={() => setMusicDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Music</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={musicForm.title}
              onChange={(e) => setMusicForm({ ...musicForm, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Genre"
              value={musicForm.genre}
              onChange={(e) => setMusicForm({ ...musicForm, genre: e.target.value })}
            />
            <TextField
              fullWidth
              type="date"
              label="Release Date"
              value={musicForm.releaseDate}
              onChange={(e) => setMusicForm({ ...musicForm, releaseDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={musicForm.isExplicit}
                  onChange={(e) => setMusicForm({ ...musicForm, isExplicit: e.target.checked })}
                />
              }
              label="Explicit Content"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={musicForm.private}
                  onChange={(e) => setMusicForm({ ...musicForm, private: e.target.checked })}
                />
              }
              label="Private"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={musicForm.isNFT}
                  onChange={(e) => setMusicForm({ ...musicForm, isNFT: e.target.checked, nftQuantity: e.target.checked ? 1 : 0 })}
                />
              }
              label="Is this a collectible/NFT?"
            />
            
            {musicForm.isNFT && (
              <TextField
                fullWidth
                type="number"
                label="Number of NFTs/Collectibles"
                value={musicForm.nftQuantity}
                onChange={(e) => setMusicForm({ ...musicForm, nftQuantity: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 1 }}
              />
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Owners & Revenue Splits
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const newCollaborator = { email: '', percentage: 0, role: 'owner' };
                  setMusicForm({ ...musicForm, collaborators: [...musicForm.collaborators, newCollaborator] });
                }}
                startIcon={<AddIcon />}
                sx={{ mb: 2 }}
              >
                Add Owner
              </Button>
              
              {musicForm.collaborators.map((collab, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Email"
                    value={collab.email}
                    onChange={(e) => {
                      const updated = [...musicForm.collaborators];
                      updated[index].email = e.target.value;
                      setMusicForm({ ...musicForm, collaborators: updated });
                    }}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Percentage"
                    value={collab.percentage}
                    onChange={(e) => {
                      const updated = [...musicForm.collaborators];
                      updated[index].percentage = parseInt(e.target.value) || 0;
                      setMusicForm({ ...musicForm, collaborators: updated });
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => {
                      const updated = musicForm.collaborators.filter((_, i) => i !== index);
                      setMusicForm({ ...musicForm, collaborators: updated });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              {musicForm.collaborators.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Total: {musicForm.collaborators.reduce((sum, c) => sum + c.percentage, 0)}% 
                  (Should equal 100% if splitting all revenue)
                </Typography>
              )}
            </Box>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<MusicIcon />}
            >
              Select Music File
              <input
                id="music-file"
                type="file"
                hidden
                accept="audio/*"
              />
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
            >
              Select Thumbnail
              <input
                id="music-thumbnail"
                type="file"
                hidden
                accept="image/*"
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMusicDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadMusic} disabled={saving}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Upload Dialog */}
      <Dialog open={videoDialog} onClose={() => setVideoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Video</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={videoForm.description}
              onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={videoForm.private}
                  onChange={(e) => setVideoForm({ ...videoForm, private: e.target.checked })}
                />
              }
              label="Private"
            />
            <TextField
              fullWidth
              select
              label="Select Studio/Playlist"
              value={videoForm.studioId}
              onChange={(e) => setVideoForm({ ...videoForm, studioId: e.target.value })}
              SelectProps={{ native: true }}
              helperText="Choose which studio/playlist this video belongs to"
            >
              <option value="">-- Create New Studio --</option>
              {userStudios.map((studio) => (
                <option key={studio._id} value={studio._id}>
                  {studio.title || studio.name}
                </option>
              ))}
            </TextField>
            <Button
              variant="outlined"
              component="label"
              startIcon={<VideoIcon />}
            >
              Select Video File
              <input
                id="video-file"
                type="file"
                hidden
                accept="video/*"
              />
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
            >
              Select Thumbnail
              <input
                id="video-thumbnail"
                type="file"
                hidden
                accept="image/*"
              />
            </Button>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Video Requirements:</strong><br />
                • Format: MP4 (H.264 codec recommended)<br />
                • Maximum file size: 500MB<br />
                • Supported formats: MP4, WebM, MOV<br />
                • For best compatibility, use MP4 with H.264 video and AAC audio
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadVideo} disabled={saving}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Studio Creation Dialog */}
      <Dialog open={studioDialog} onClose={() => setStudioDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Studio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Studio Name"
              value={studioForm.name}
              onChange={(e) => setStudioForm({ ...studioForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={studioForm.description}
              onChange={(e) => setStudioForm({ ...studioForm, description: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={studioForm.isPrivate}
                  onChange={(e) => setStudioForm({ ...studioForm, isPrivate: e.target.checked })}
                />
              }
              label="Private Studio"
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
            >
              Select Thumbnail (Optional)
              <input
                id="studio-thumbnail"
                type="file"
                hidden
                accept="image/*"
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudioDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateStudio} disabled={saving}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Album Upload Dialog */}
      <Dialog 
        open={albumDialog} 
        onClose={() => setAlbumDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Album Upload</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The bulk album upload feature allows you to upload an entire album with multiple tracks at once.
          </Alert>
          <Typography variant="body1" paragraph>
            You will be redirected to the bulk upload page with this user pre-selected.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Features include:
          </Typography>
          <ul>
            <li>Upload multiple tracks at once</li>
            <li>Use a single album cover for all tracks</li>
            <li>Set consistent royalty splits across all tracks</li>
            <li>Automatic track ordering based on filename</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlbumDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AlbumIcon />}
            onClick={() => {
              // Navigate to bulk upload page with userId
              history.push(`/admin/bulk-album-upload?userId=${userId}`);
            }}
          >
            Go to Bulk Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}